/**
 * JavaScript Translation Service
 * Provides translation functionality for client-side JavaScript
 */
class TranslationService {
  constructor() {
    this.translations = {};
    this.locale = 'fr'; // Default locale
    this.fallbackLocale = 'fr';
    this.loadTranslations();
  }

  /**
   * Load translations from the DOM
   */
  loadTranslations() {
    try {
      let data = null;

      // Try to load from script tag first
      const scriptTag = document.getElementById('js-translations');
      if (scriptTag) {
        data = JSON.parse(scriptTag.textContent);
      }

      // Try to load from data attribute on body
      if (!data) {
        const bodyTranslations = document.body.dataset.translations;
        if (bodyTranslations) {
          data = JSON.parse(bodyTranslations);
        }
      }

      // Try to load from meta tag
      if (!data) {
        const metaTag = document.querySelector('meta[name="js-translations"]');
        if (metaTag) {
          data = JSON.parse(metaTag.content);
        }
      }

      if (data) {
        // Handle new format with metadata
        if (data.translations) {
          this.translations = data.translations;
          this.locale = data.locale || this.locale;
          this.fallbackLocale = data.fallback_locale || this.fallbackLocale;
        } else {
          // Handle legacy format
          this.translations = data;
        }
      } else {
        console.warn('No translations found in DOM');
        this.translations = {};
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      this.translations = {};
    }
  }

  /**
   * Get current locale
   */
  getLocale() {
    return this.locale;
  }

  /**
   * Set current locale
   */
  setLocale(locale) {
    this.locale = locale;
  }

  /**
   * Translate a key with optional interpolations
   * @param {string} key - Translation key (dot notation)
   * @param {Object} options - Options including count, interpolations
   * @returns {string} Translated text
   */
  t(key, options = {}) {
    // If translations are not yet loaded, try to load them again
    // This handles cases where the service is initialized before the DOM is fully ready
    if (Object.keys(this.translations).length === 0) {
      this.loadTranslations();
    }

    try {
      let translation = this.getTranslation(key);
      
      if (translation === null || translation === undefined) {
        return this.handleMissingTranslation(key, options);
      }

      // Handle pluralization
      if (typeof translation === 'object' && options.count !== undefined) {
        translation = this.pluralize(translation, options.count);
      }

      // Handle interpolations
      if (typeof translation === 'string' && options) {
        translation = this.interpolate(translation, options);
      }

      return translation;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      return this.handleMissingTranslation(key, options);
    }
  }

  /**
   * Get translation by key path
   * @param {string} key - Dot notation key
   * @returns {*} Translation value
   */
  getTranslation(key) {
    const keys = key.split('.');
    let current = this.translations;

    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        return null;
      }
    }

    return current;
  }

  /**
   * Handle pluralization
   * @param {Object} translations - Plural translations object
   * @param {number} count - Count for pluralization
   * @returns {string} Pluralized translation
   */
  pluralize(translations, count) {
    if (typeof translations !== 'object') {
      return translations;
    }

    // Handle French pluralization rules
    if (this.locale === 'fr') {
      if (count === 0 && translations.zero) {
        return translations.zero;
      } else if (count === 1 && translations.one) {
        return translations.one;
      } else if (translations.other) {
        return translations.other;
      }
    }

    // Handle English pluralization rules
    if (this.locale === 'en') {
      if (count === 0 && translations.zero) {
        return translations.zero;
      } else if (count === 1 && translations.one) {
        return translations.one;
      } else if (translations.other) {
        return translations.other;
      }
    }

    // Fallback to 'other' or first available
    return translations.other || translations.one || Object.values(translations)[0] || '';
  }

  /**
   * Interpolate variables in translation string
   * @param {string} translation - Translation string with placeholders
   * @param {Object} options - Interpolation variables
   * @returns {string} Interpolated string
   */
  interpolate(translation, options) {
    let result = translation;

    // Replace %{variable} patterns
    Object.keys(options).forEach(key => {
      if (key !== 'count') {
        const pattern = new RegExp(`%\\{${key}\\}`, 'g');
        result = result.replace(pattern, options[key]);
      }
    });

    // Replace {{variable}} patterns (alternative syntax)
    Object.keys(options).forEach(key => {
      if (key !== 'count') {
        const pattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        result = result.replace(pattern, options[key]);
      }
    });

    return result;
  }

  /**
   * Handle missing translations
   * @param {string} key - Missing translation key
   * @param {Object} options - Translation options
   * @returns {string} Fallback text
   */
  handleMissingTranslation(key, options = {}) {
    const fallbackText = `[missing "${key}" translation]`;
    
    // Log missing translation in development
    if (this.isDevelopment()) {
      console.warn(`Missing translation: ${key}`);
    }

    // Try to provide a reasonable fallback
    const lastKey = key.split('.').pop();
    if (lastKey) {
      // Convert snake_case to Title Case
      const humanized = lastKey.replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase());
      return humanized;
    }

    return fallbackText;
  }

  /**
   * Check if we're in development environment
   * @returns {boolean}
   */
  isDevelopment() {
    return document.body.dataset.environment === 'development' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  /**
   * Check if a translation exists
   * @param {string} key - Translation key
   * @returns {boolean}
   */
  exists(key) {
    return this.getTranslation(key) !== null;
  }

  /**
   * Get all translations for a scope
   * @param {string} scope - Translation scope
   * @returns {Object} Scoped translations
   */
  scope(scope) {
    return this.getTranslation(scope) || {};
  }

  /**
   * Reload translations from DOM
   */
  reload() {
    this.loadTranslations();
  }
}

// Create global instance
window.TranslationService = new TranslationService();

// Export for ES6 modules
export default TranslationService;